import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { GroupBillingCard } from "@/components/group/GroupBillingCard";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  LayoutDashboard, Building2, TrendingUp, ListChecks, AlertTriangle,
  Sparkles, FileText, Settings, ChevronRight, ArrowUpRight, ArrowDownRight,
  Send, Loader2, Star, LogOut, BarChart3, Target,
} from "lucide-react";

import { MissionsView, RadarWidget } from "./MissionsView";
import KPIBouquetsStrip from '../../components/cockpit/KPIBouquetsStrip';
import GroupPilotageTable from '../../components/pilotage/GroupPilotageTable';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const SeverityChip = ({ level }) => {
  const map = {
    high: { label: "Élevé", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
    medium: { label: "Moyen", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
    low: { label: "Faible", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
    info: { label: "Info", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  };
  const c = map[level] || map.low;
  return <span className={`text-xs px-2 py-1 rounded-md border ${c.cls}`} data-testid={`severity-${level}`}>{c.label}</span>;
};

const TrendArrow = ({ value }) => {
  if (value === 0 || value === undefined || value === null) return <span className="text-white/40">—</span>;
  if (value > 0)
    return <span className="text-emerald-400 inline-flex items-center gap-0.5"><ArrowUpRight className="w-3.5 h-3.5" />+{value}%</span>;
  return <span className="text-red-400 inline-flex items-center gap-0.5"><ArrowDownRight className="w-3.5 h-3.5" />{value}%</span>;
};

const KpiCard = ({ label, value, sub, hint, accent, testId }) => (
  <Card className="relative p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15 overflow-hidden" data-testid={testId}>
    <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#A855F7]/8 rounded-full blur-3xl pointer-events-none" />
    <div className="relative">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5" style={MONO}>{label}</div>
      {hint && <div className="text-[10px] text-white/30 mb-3 leading-tight">{hint}</div>}
      <div className={`text-3xl font-bold ${accent || "text-white"}`} style={DISPLAY}>{value}</div>
      {sub && <div className="text-xs text-white/40 mt-2">{sub}</div>}
    </div>
  </Card>
);

const ScoreBar = ({ score, max = 100 }) => {
  const pct = Math.min(100, Math.max(0, (score / max) * 100));
  const color = score >= 80 ? "bg-emerald-500" : score >= 65 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// ====================================================================
// VUE GROUPE
// ====================================================================
const OverviewView = ({ overview, onOpenAgency, groupId, onGoMissions }) => {
  if (!overview) return null;
  const m = overview.metrics;
  return (
    <div className="space-y-6" data-testid="group-overview-view">
      {/* Bandeau explicatif intro */}
      <div className="text-sm text-white/55 max-w-3xl">
        Vision <span className="text-white/85 font-medium">synthétique de la santé du réseau</span> sur 30 jours. Pas de détail vendeur ou agent — c&apos;est le rôle des dashboards d&apos;agence.
      </div>

      {/* Vue synthétique — 5 bouquets KPI agence (P3) */}
      <div data-testid="group-bouquets-strip">
        <KPIBouquetsStrip scope="agency" variant="admin" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Score groupe"
          hint="Moyenne pondérée des scores agences (sur 100)"
          value={`${m.score_groupe}`}
          sub={<><TrendArrow value={m.trend} /> · vs mois précédent</>}
          testId="kpi-score-groupe"
        />
        <KpiCard
          label="Agences performantes"
          hint="Agences avec un score ≥ 80/100"
          value={`${m.n_performing} / ${m.n_agencies}`}
          sub={`${m.n_agencies - m.n_performing - m.n_at_risk} en zone moyenne`}
          accent="text-emerald-400"
          testId="kpi-performing"
        />
        <KpiCard
          label="À accompagner"
          hint="Agences en risque (score < 60 ou recul marqué)"
          value={m.n_at_risk}
          sub={`${m.n_alerts_high} alerte(s) élevée(s) · ${m.n_alerts_medium} moyenne(s)`}
          accent={m.n_at_risk > 0 ? "text-red-400" : "text-white"}
          testId="kpi-at-risk"
        />
        <KpiCard
          label="Homogénéité"
          hint="Cohérence des pratiques entre agences"
          value={`${m.homogeneity}%`}
          sub={`Écart : ${m.gap} pts entre la meilleure et la moins bonne`}
          testId="kpi-homogeneity"
        />
      </div>

      {/* Lot 5 — pilotage consolidé (entrées/sorties/CA par agence) */}
      <GroupPilotageTable groupId={groupId} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
          <div className="text-sm font-medium text-white/75 mb-1 flex items-center gap-2"><Star className="w-4 h-4 text-emerald-400" />Agences modèles</div>
          <div className="text-xs text-white/40 mb-4">Les meilleures performances du réseau — process à répliquer</div>
          <div className="space-y-3">
            {overview.top_agencies.map((a) => (
              <div key={a.agency_id} className="flex items-center justify-between cursor-pointer hover:bg-white/5/50 -mx-2 px-2 py-2 rounded" onClick={() => onOpenAgency(a.agency_id)} data-testid={`top-agency-${a.agency_id}`}>
                <div>
                  <div className="text-sm text-white/90">{a.agency_name}</div>
                  <div className="text-xs text-white/40">{a.city} · {a.director}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg text-emerald-400 font-medium">{a.score}<span className="text-xs text-white/40">/100</span></div>
                  <TrendArrow value={a.trend} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
          <div className="text-sm font-medium text-white/75 mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" />Agences à accompagner</div>
          <div className="text-xs text-white/40 mb-4">Score faible ou tendance négative — point directeur recommandé</div>
          <div className="space-y-3">
            {overview.bottom_agencies.map((a) => (
              <div key={a.agency_id} className="flex items-center justify-between cursor-pointer hover:bg-white/5/50 -mx-2 px-2 py-2 rounded" onClick={() => onOpenAgency(a.agency_id)} data-testid={`bottom-agency-${a.agency_id}`}>
                <div>
                  <div className="text-sm text-white/90">{a.agency_name}</div>
                  <div className="text-xs text-white/40">{a.city} · {a.recommendation}</div>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-medium ${a.score < 60 ? "text-red-400" : "text-amber-400"}`}>{a.score}<span className="text-xs text-white/40">/100</span></div>
                  <TrendArrow value={a.trend} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-4 sm:p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
        <div className="text-xs uppercase tracking-wider text-white/40 mb-3 sm:mb-4">Composantes du score groupe</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 text-center">
          <div>
            <div className="text-xs text-white/40">Satisfaction vendeur</div>
            <div className="text-2xl mt-1 text-white/90">{m.satisfaction_avg}<span className="text-sm text-white/40">/5</span></div>
            <div className="text-[10px] text-white/30 mt-1">Avis vendeurs · pondération 25%</div>
          </div>
          <div>
            <div className="text-xs text-white/40">Respect des process</div>
            <div className="text-2xl mt-1 text-white/90">{m.process_avg}%</div>
            <div className="text-[10px] text-white/30 mt-1">Mandats suivis · pondération 30%</div>
          </div>
          <div>
            <div className="text-xs text-white/40">Adoption Homly</div>
            <div className="text-2xl mt-1 text-white/90">{m.adoption_avg}%</div>
            <div className="text-[10px] text-white/30 mt-1">Utilisateurs actifs · pondération 20%</div>
          </div>
          <div>
            <div className="text-xs text-white/40">Dynamique commerciale</div>
            <div className="text-2xl mt-1 text-white/90">{m.dynamics_avg}%</div>
            <div className="text-[10px] text-white/30 mt-1">Évolution volume · pondération 25%</div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ====================================================================
// COMPARAISON AGENCES
// ====================================================================
const AgenciesView = ({ agencies, onOpenAgency }) => {
  const [sortKey, setSortKey] = useState("score");
  const sorted = [...(agencies || [])].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  return (
    <div className="space-y-4" data-testid="agencies-view">
      <div>
        <h2 className="text-lg text-white/90">Comparatif de vos {sorted.length} agences</h2>
        <p className="text-sm text-white/55 mt-1">
          Toutes les agences du groupe classées par score. <span className="text-white/75">Cliquez sur une ligne</span> pour voir la fiche synthétique et préparer un point avec son directeur. Tri : <span className="text-emerald-400">{({score:"Score global", process_score:"Process", satisfaction_score:"Satisfaction", adoption_score:"Adoption"})[sortKey]}</span>.
        </p>
      </div>
      <Card className="bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/30 text-white/55 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Agence</th>
              <th className="text-left px-4 py-3">Directeur</th>
              <th className="text-right px-4 py-3 cursor-pointer hover:text-emerald-400" onClick={() => setSortKey("score")}>Score /100</th>
              <th className="text-right px-4 py-3">Évol. 30j</th>
              <th className="text-right px-4 py-3 cursor-pointer hover:text-emerald-400" onClick={() => setSortKey("process_score")}>Process</th>
              <th className="text-right px-4 py-3 cursor-pointer hover:text-emerald-400" onClick={() => setSortKey("satisfaction_score")}>Satisfaction</th>
              <th className="text-right px-4 py-3 cursor-pointer hover:text-emerald-400" onClick={() => setSortKey("adoption_score")}>Adoption</th>
              <th className="text-center px-4 py-3">Niveau</th>
              <th className="text-left px-4 py-3">Action suggérée</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((a) => (
              <tr key={a.agency_id} className="border-t border-white/5 hover:bg-white/5/40 cursor-pointer" onClick={() => onOpenAgency(a.agency_id)} data-testid={`agency-row-${a.agency_id}`}>
                <td className="px-4 py-3">
                  <div className="text-white/90 flex items-center gap-1.5">
                    {a.agency_name}
                    {a.has_estimated_data && (
                      <span
                        data-testid={`estimated-badge-${a.agency_id}`}
                        title={`Données insuffisantes — scores estimés : ${(a.estimated_fields || []).join(', ')}. Méthode : estimation déterministe en attendant plus de données réelles.`}
                        className="text-[9px] px-1.5 py-0.5 rounded-full border border-amber-500/40 text-amber-400 bg-amber-500/10 uppercase tracking-wide cursor-help"
                      >
                        Estimation
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/40">{a.city}</div>
                </td>
                <td className="px-4 py-3 text-white/55">{a.director}</td>
                <td className="px-4 py-3 text-right">
                  <div className={`font-medium text-lg ${a.score >= 80 ? "text-emerald-400" : a.score >= 65 ? "text-white/90" : "text-red-400"}`}>{a.score}</div>
                </td>
                <td className="px-4 py-3 text-right"><TrendArrow value={a.trend} /></td>
                <td className="px-4 py-3 text-right text-white/75">{a.process_score}%</td>
                <td className="px-4 py-3 text-right text-white/75">{a.satisfaction_rating}<span className="text-xs text-white/40">/5</span></td>
                <td className="px-4 py-3 text-right text-white/75">{a.adoption_score}%</td>
                <td className="px-4 py-3 text-center"><SeverityChip level={a.alert_level} /></td>
                <td className="px-4 py-3 text-white/55 text-xs">{a.recommendation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </Card>
    </div>
  );
};

// ====================================================================
// TENDANCES
// ====================================================================
const Sparkline = ({ series, accessor, color, height = 180, suffix = "" }) => {
  if (!series || series.length < 2) return null;
  const W = 800;
  const H = height;
  const padX = 40;
  const padY = 30;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;
  const values = series.map(accessor);
  const vMin = Math.min(...values);
  const vMax = Math.max(...values);
  const range = Math.max(0.01, vMax - vMin);
  // Si plage trop petite, élargit pour donner du relief visuel
  const padRange = range < 5 ? Math.max(5, range) : range;
  const yMin = Math.max(0, vMin - padRange * 0.3);
  const yMax = vMax + padRange * 0.3;
  const span = Math.max(0.01, yMax - yMin);

  const points = series.map((p, i) => {
    const x = padX + (i / (series.length - 1)) * innerW;
    const y = padY + (1 - (accessor(p) - yMin) / span) * innerH;
    return { x, y, v: accessor(p) };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${H - padY} L ${points[0].x} ${H - padY} Z`;
  const isLast = (i) => i === points.length - 1;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${color.replace("#", "")}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Grille horizontale */}
      {[0.25, 0.5, 0.75].map((t, i) => (
        <line key={i} x1={padX} x2={W - padX} y1={padY + t * innerH} y2={padY + t * innerH} stroke="white" strokeOpacity="0.05" strokeDasharray="4 4" />
      ))}
      {/* Axe Y labels (min, mid, max) */}
      <text x="8" y={padY + 4} fill="white" fillOpacity="0.35" fontSize="11" fontFamily="JetBrains Mono">{Math.round(yMax)}{suffix}</text>
      <text x="8" y={padY + innerH + 4} fill="white" fillOpacity="0.35" fontSize="11" fontFamily="JetBrains Mono">{Math.round(yMin)}{suffix}</text>
      {/* Zone area */}
      <path d={areaPath} fill={`url(#grad-${color.replace("#", "")})`} />
      {/* Ligne */}
      <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Points + labels */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={isLast(i) ? 6 : 3.5} fill={isLast(i) ? color : "#0c0814"} stroke={color} strokeWidth="2" />
          <text x={p.x} y={p.y - 12} fill={isLast(i) ? color : "white"} fillOpacity={isLast(i) ? 1 : 0.7} fontSize={isLast(i) ? 13 : 11} fontWeight={isLast(i) ? "700" : "400"} textAnchor="middle" fontFamily="Space Grotesk">{p.v}{suffix}</text>
        </g>
      ))}
    </svg>
  );
};

const TrendsView = ({ trends }) => {
  if (!trends) return null;
  // Labels temporels lisibles
  const labels = trends.series.map((p) => {
    if (p.day_offset === 0) return "Aujourd'hui";
    const m = Math.abs(p.day_offset);
    if (m === 30) return "1 mois";
    if (m === 60) return "2 mois";
    if (m === 90) return "3 mois";
    return `${m}j`;
  });

  // Lecture en clair de la tendance
  const d30 = trends.delta_30d;
  const d90 = trends.delta_90d;
  const readScore =
    d30 > 3 ? { txt: "Le groupe progresse nettement sur le dernier mois.", color: "text-emerald-400", icon: "↗" }
      : d30 > 0 ? { txt: "Légère progression. À consolider.", color: "text-emerald-300", icon: "↗" }
      : d30 > -3 ? { txt: "Stable sur 30 jours. Rester vigilant sur les agences à risque.", color: "text-white/65", icon: "→" }
      : d30 > -10 ? { txt: "Recul léger. Un point réseau est conseillé.", color: "text-amber-400", icon: "↘" }
      : { txt: "Recul marqué. Action prioritaire à engager avec les directeurs.", color: "text-red-400", icon: "↘" };

  const homoNow = trends.series[trends.series.length - 1].homogeneity;
  const homoOld = trends.series[0].homogeneity;
  const homoDelta = homoNow - homoOld;
  const readHomo =
    homoNow >= 80 ? { txt: `Vos agences sont très homogènes (${homoNow}%). Les pratiques sont alignées.`, color: "text-emerald-400", icon: "●" }
      : homoNow >= 65 ? { txt: `Homogénéité correcte (${homoNow}%). Quelques disparités à surveiller.`, color: "text-white/75", icon: "●" }
      : { txt: `Hétérogénéité élevée (${homoNow}%). Vos agences s'écartent les unes des autres.`, color: "text-amber-400", icon: "●" };
  const homoTrendTxt = homoDelta > 2 ? " Tendance à l'amélioration." : homoDelta < -2 ? " Tendance à la dispersion." : "";

  return (
    <div className="space-y-6" data-testid="trends-view">
      <div>
        <h2 className="text-xl text-white tracking-tight" style={DISPLAY}>Tendances</h2>
        <p className="text-sm text-white/55 mt-1">Évolution sur 90 jours · échantillonnée tous les 15 jours.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Évolution 30j" hint="Δ score sur le mois écoulé" value={<TrendArrow value={trends.delta_30d} />} testId="kpi-delta-30" />
        <KpiCard label="Évolution 90j" hint="Δ score sur le trimestre" value={<TrendArrow value={trends.delta_90d} />} testId="kpi-delta-90" />
        <KpiCard label="Meilleure agence" hint="Score le plus haut du réseau" value={trends.best_score} accent="text-emerald-400" testId="kpi-best" />
        <KpiCard label="Écart inter-agences" hint="Distance meilleure / moins bonne" value={`${trends.gap} pts`} testId="kpi-gap" />
      </div>

      {/* Graphique Score groupe — SPARKLINE */}
      <Card className="p-6 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
        <div className="flex items-start justify-between mb-1 gap-4">
          <div>
            <div className="text-sm font-medium text-white" style={DISPLAY}>Évolution du score groupe</div>
            <p className="text-xs text-white/40 mt-1">Score moyen pondéré sur 90 jours</p>
          </div>
          <div className={`text-right text-xs ${readScore.color} max-w-xs`}>
            <div className="text-2xl mb-1" style={DISPLAY}>{readScore.icon} {d30 > 0 ? "+" : ""}{d30}%</div>
            <div className="text-[11px] leading-snug">{readScore.txt}</div>
          </div>
        </div>
        <div className="mt-4">
          <Sparkline series={trends.series} accessor={(p) => p.score} color="#C084FC" height={200} />
          <div className="grid mt-2" style={{ gridTemplateColumns: `repeat(${labels.length}, 1fr)` }}>
            {labels.map((l, i) => (
              <div key={i} className="text-center text-[10px] text-white/40" style={MONO}>{l}</div>
            ))}
          </div>
        </div>
      </Card>

      {/* Graphique Homogénéité — SPARKLINE */}
      <Card className="p-6 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
        <div className="flex items-start justify-between mb-1 gap-4">
          <div>
            <div className="text-sm font-medium text-white" style={DISPLAY}>Homogénéité inter-agences</div>
            <p className="text-xs text-white/40 mt-1">À quel point vos agences se ressemblent. <span className="text-white/65">100% = toutes au même niveau · 0% = très dispersées.</span></p>
          </div>
          <div className={`text-right text-xs ${readHomo.color} max-w-xs`}>
            <div className="text-2xl mb-1" style={DISPLAY}>{readHomo.icon} {homoNow}%</div>
            <div className="text-[11px] leading-snug">{readHomo.txt}{homoTrendTxt}</div>
          </div>
        </div>
        <div className="mt-4">
          <Sparkline series={trends.series} accessor={(p) => p.homogeneity} color="#38bdf8" height={160} suffix="%" />
          <div className="grid mt-2" style={{ gridTemplateColumns: `repeat(${labels.length}, 1fr)` }}>
            {labels.map((l, i) => (
              <div key={i} className="text-center text-[10px] text-white/40" style={MONO}>{l}</div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

// ====================================================================
// PROCESS
// ====================================================================
const ProcessView = ({ processData, onOpenAgency, groupId }) => {
  const [briefAgency, setBriefAgency] = useState(null);
  const [briefText, setBriefText] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!processData) return null;

  const genBrief = async (aid) => {
    setBriefAgency(aid);
    setBriefText(null);
    setBriefLoading(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/agencies/${aid}/director-brief`);
      setBriefText(data.brief);
    } catch (e) {
      setBriefText("Erreur : " + (e.response?.data?.detail || e.message));
    } finally { setBriefLoading(false); }
  };

  const copyBrief = () => {
    if (!briefText) return;
    navigator.clipboard.writeText(briefText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6" data-testid="process-view">
      <div>
        <h2 className="text-xl text-white tracking-tight" style={DISPLAY}>Conformité des process</h2>
        <p className="text-sm text-white/55 mt-1">Suivi de l&apos;application des standards Homly (mandats, signatures, suivi vendeur). <span className="text-white/85">Cliquez sur une agence</span> pour ouvrir sa fiche ou préparer un brief.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Process moyen groupe" hint="% des mandats correctement suivis" value={`${processData.process_avg}%`} testId="kpi-process-avg" />
        <KpiCard label="Homogénéité" hint="Cohérence inter-agences" value={`${processData.homogeneity}%`} accent="text-sky-400" testId="kpi-process-homo" />
        <KpiCard label="Agences modèles" hint="Process à répliquer" value={processData.models.length} accent="text-emerald-400" testId="kpi-models" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Modèles */}
        <Card className="p-5 bg-gradient-to-br from-emerald-950/20 to-[#0c0814]/80 border-emerald-500/20">
          <div className="text-sm font-medium text-emerald-400 mb-1 flex items-center gap-2" style={DISPLAY}><Star className="w-4 h-4" />Process à répliquer</div>
          <div className="text-xs text-white/40 mb-4">Inspirez-vous de ces agences pour relever le niveau du réseau.</div>
          {processData.models.map((m) => (
            <div key={m.agency_id} className="py-3 border-b border-white/5 last:border-0" data-testid={`model-${m.agency_id}`}>
              <div className="flex justify-between items-center text-sm mb-2">
                <button onClick={() => onOpenAgency(m.agency_id)} className="text-white hover:text-emerald-400 transition-colors text-left" data-testid={`open-model-${m.agency_id}`}>
                  {m.agency_name} <span className="text-white/30 group-hover:text-emerald-400">→</span>
                </button>
                <span className="text-emerald-400 font-bold" style={DISPLAY}>{m.process_score}%</span>
              </div>
              <ScoreBar score={m.process_score} />
            </div>
          ))}
        </Card>

        {/* À accompagner — actionnables */}
        <Card className="p-5 bg-gradient-to-br from-amber-950/20 to-[#0c0814]/80 border-amber-500/20">
          <div className="text-sm font-medium text-amber-400 mb-1 flex items-center gap-2" style={DISPLAY}><AlertTriangle className="w-4 h-4" />Agences à accompagner</div>
          <div className="text-xs text-white/40 mb-4">Process en deçà de la moyenne groupe. Ouvrez la fiche ou préparez un brief Providence.</div>
          {processData.to_support.length === 0 ? (
            <div className="text-sm text-white/40">Aucune agence sous la moyenne — bravo !</div>
          ) : processData.to_support.map((m) => (
            <div key={m.agency_id} className="py-3 border-b border-white/5 last:border-0" data-testid={`to-support-${m.agency_id}`}>
              <div className="flex justify-between items-center text-sm mb-2">
                <button onClick={() => onOpenAgency(m.agency_id)} className="text-white hover:text-amber-400 transition-colors text-left" data-testid={`open-support-${m.agency_id}`}>
                  {m.agency_name} <span className="text-white/30">→</span>
                </button>
                <span className="text-amber-400 font-bold" style={DISPLAY}>{m.process_score}% <span className="text-xs text-white/40 font-normal">(-{m.gap})</span></span>
              </div>
              <ScoreBar score={m.process_score} />
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => genBrief(m.agency_id)} disabled={briefLoading && briefAgency === m.agency_id} className="bg-gradient-to-r from-[#A855F7] to-[#C084FC] hover:opacity-90 text-white text-xs h-7" data-testid={`brief-${m.agency_id}`}>
                  {briefLoading && briefAgency === m.agency_id ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Brief…</> : <><Sparkles className="w-3 h-3 mr-1.5" />Préparer le brief</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => onOpenAgency(m.agency_id)} className="border-white/15 text-white/75 hover:bg-white/5 text-xs h-7" data-testid={`detail-${m.agency_id}`}>
                  Voir détail
                </Button>
              </div>
              {briefText && briefAgency === m.agency_id && (
                <div className="mt-3 bg-gradient-to-br from-purple-950/40 to-pink-950/30 border border-purple-500/30 rounded-lg p-3" data-testid={`brief-content-${m.agency_id}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[9px] uppercase tracking-widest text-purple-300 font-medium">Brief Providence · prêt à coller</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={copyBrief} className="h-6 text-xs">{copied ? "Copié ✓" : "Copier"}</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setBriefText(null); setBriefAgency(null); }} className="h-6 text-xs">Fermer</Button>
                    </div>
                  </div>
                  <div className="text-xs text-white whitespace-pre-wrap leading-relaxed">{briefText}</div>
                </div>
              )}
            </div>
          ))}
        </Card>
      </div>

      {/* Tableau global */}
      <Card className="bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15 overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5">
          <div className="text-sm font-medium text-white" style={DISPLAY}>Vue détaillée par agence</div>
          <p className="text-xs text-white/40 mt-1">Cliquez sur une ligne pour ouvrir la fiche.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/30 text-white/55 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Agence</th>
                <th className="text-right px-4 py-3">Process</th>
                <th className="text-right px-4 py-3">Satisfaction</th>
                <th className="text-right px-4 py-3">Adoption</th>
              </tr>
            </thead>
            <tbody>
              {processData.agencies.map((a) => (
                <tr key={a.agency_id} onClick={() => onOpenAgency(a.agency_id)} className="border-t border-white/5 hover:bg-[#A855F7]/5 cursor-pointer" data-testid={`process-row-${a.agency_id}`}>
                  <td className="px-4 py-3 text-white">{a.agency_name}</td>
                  <td className="px-4 py-3 text-right text-white/75">{a.process_score}%</td>
                  <td className="px-4 py-3 text-right text-white/75">{a.satisfaction_score}%</td>
                  <td className="px-4 py-3 text-right text-white/75">{a.adoption_score}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ====================================================================
// ALERTES
// ====================================================================
const AlertsView = ({ alertsData, agencies, onOpenAgency, groupId }) => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem(`group-alerts-dismissed-${groupId}`);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const [briefAgency, setBriefAgency] = useState(null);
  const [briefText, setBriefText] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!alertsData) return null;

  const dismiss = (alert) => {
    const id = alert.id;
    const next = new Set([...dismissed, id]);
    setDismissed(next);
    localStorage.setItem(`group-alerts-dismissed-${groupId}`, JSON.stringify([...next]));
    // Log côté serveur pour la mémoire managériale (1 log par agence concernée)
    (alert.agencies || []).forEach((aid) => {
      api.post(`/groups/${groupId}/agencies/${aid}/log-action`, {
        action_type: "alert_dismissed",
        metadata: { alert_id: id, alert_title: alert.title },
      }).catch(() => {});
    });
  };
  const reset = () => {
    setDismissed(new Set());
    localStorage.removeItem(`group-alerts-dismissed-${groupId}`);
  };

  const visible = alertsData.alerts.filter((a) => !dismissed.has(a.id));

  // helper : trouve nom agence depuis id
  const agencyById = (aid) => agencies?.find((a) => a.agency_id === aid);

  const genBrief = async (aid) => {
    setBriefAgency(aid);
    setBriefText(null);
    setBriefLoading(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/agencies/${aid}/director-brief`);
      setBriefText(data.brief);
    } catch (e) {
      setBriefText("Erreur : " + (e.response?.data?.detail || e.message));
    } finally { setBriefLoading(false); }
  };

  const copyBrief = () => {
    if (!briefText) return;
    navigator.clipboard.writeText(briefText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6" data-testid="alerts-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl text-white tracking-tight" style={DISPLAY}>Alertes groupe</h2>
          <p className="text-sm text-white/55 mt-1">Signaux faibles détectés par Providence. Chaque alerte est <span className="text-white/85">actionnable</span> : voir l&apos;agence, préparer un brief, ou marquer comme traitée.</p>
        </div>
        {dismissed.size > 0 && (
          <Button size="sm" variant="ghost" onClick={reset} data-testid="reset-alerts">Réafficher les {dismissed.size} alerte(s) masquée(s)</Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <KpiCard label="Alertes élevées" hint="Action prioritaire" value={alertsData.n_high} accent="text-red-400" testId="kpi-alert-high" />
        <KpiCard label="Alertes moyennes" hint="À surveiller" value={alertsData.n_medium} accent="text-amber-400" testId="kpi-alert-medium" />
        <KpiCard label="Signaux positifs" hint="Bonnes pratiques détectées" value={alertsData.n_info} accent="text-emerald-400" testId="kpi-alert-info" />
      </div>

      <div className="space-y-3">
        {visible.length === 0 && (
          <Card className="p-10 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15 text-center text-white/55">
            Aucune alerte active. {dismissed.size > 0 && "Les alertes que vous avez traitées peuvent être réaffichées via le bouton ci-dessus."}
          </Card>
        )}
        {visible.map((alert) => {
          const targetAgencies = (alert.agencies || []).slice(0, 4);
          const sevBorder = {
            high: "border-red-500/30 bg-gradient-to-br from-red-950/20 to-[#0c0814]/80",
            medium: "border-amber-500/30 bg-gradient-to-br from-amber-950/15 to-[#0c0814]/80",
            info: "border-emerald-500/30 bg-gradient-to-br from-emerald-950/15 to-[#0c0814]/80",
            low: "border-white/10",
          }[alert.severity] || "border-white/10";

          return (
            <Card key={alert.id} className={`p-5 border ${sevBorder}`} data-testid={`alert-${alert.id}`}>
              <div className="flex items-start gap-4">
                <SeverityChip level={alert.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium" style={DISPLAY}>{alert.title}</div>
                  <div className="text-sm text-white/55 mt-1">{alert.detail}</div>
                  <div className="text-xs text-emerald-400 mt-3 flex items-center gap-1.5">
                    <ChevronRight className="w-3.5 h-3.5" />{alert.action}
                  </div>

                  {/* CHIPS AGENCES CLIQUABLES */}
                  {targetAgencies.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {targetAgencies.map((aid) => {
                        const ag = agencyById(aid);
                        if (!ag) return null;
                        return (
                          <button
                            key={aid}
                            onClick={() => onOpenAgency(aid)}
                            className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-[#A855F7]/15 border border-white/10 hover:border-[#A855F7]/40 text-xs text-white/75 hover:text-white transition-all"
                            data-testid={`alert-agency-${aid}`}
                          >
                            <Building2 className="w-3 h-3 opacity-60" />
                            {ag.agency_name}
                            <span className="text-[10px] text-white/40 group-hover:text-[#C084FC]">→</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* ACTIONS */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {targetAgencies.length === 1 && alert.severity !== "info" && (
                      <Button
                        size="sm"
                        className="bg-gradient-to-r from-[#A855F7] to-[#C084FC] hover:opacity-90 text-white"
                        onClick={() => genBrief(targetAgencies[0])}
                        disabled={briefLoading && briefAgency === targetAgencies[0]}
                        data-testid={`alert-brief-${alert.id}`}
                      >
                        {briefLoading && briefAgency === targetAgencies[0] ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Brief…</> : <><Sparkles className="w-3 h-3 mr-1.5" />Préparer le brief directeur</>}
                      </Button>
                    )}
                    {targetAgencies.length >= 1 && (
                      <Button size="sm" variant="outline" onClick={() => onOpenAgency(targetAgencies[0])} className="border-white/15 text-white/85 hover:bg-white/5" data-testid={`alert-open-${alert.id}`}>
                        Voir la fiche {targetAgencies.length > 1 ? "(1ère agence)" : ""}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => dismiss(alert)} className="text-white/55 hover:text-white" data-testid={`alert-dismiss-${alert.id}`}>
                      Marquer comme traitée
                    </Button>
                  </div>

                  {/* BRIEF GENERATED INLINE */}
                  {briefText && briefAgency && targetAgencies.includes(briefAgency) && (
                    <div className="mt-4 bg-gradient-to-br from-purple-950/40 to-pink-950/30 border border-purple-500/30 rounded-lg p-4" data-testid="alert-brief-content">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[10px] uppercase tracking-widest text-purple-300 font-medium">Brief Providence · prêt à coller</span>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={copyBrief}>{copied ? "Copié ✓" : "Copier"}</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setBriefText(null); setBriefAgency(null); }}>Fermer</Button>
                        </div>
                      </div>
                      <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">{briefText}</div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ====================================================================
// FICHE AGENCE
// ====================================================================
const AgencyDetailPanel = ({ detail, groupId, onClose }) => {
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [history, setHistory] = useState(null);

  // Charge la timeline d'actions managériales
  useEffect(() => {
    if (!detail || !groupId) return;
    api.get(`/groups/${groupId}/agencies/${detail.agency_id}/action-history`)
      .then((r) => setHistory(r.data))
      .catch(() => setHistory(null));
  }, [detail, groupId]);

  if (!detail) return null;
  const m = detail.metrics_30d;

  const generateBrief = async () => {
    setBriefLoading(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/agencies/${detail.agency_id}/director-brief`);
      setBrief(data.brief);
    } catch (e) {
      setBrief("Erreur : " + (e.response?.data?.detail || e.message));
    } finally {
      setBriefLoading(false);
    }
  };

  const copyBrief = () => {
    if (!brief) return;
    navigator.clipboard.writeText(brief);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="agency-detail-panel">
      <Card className="w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto bg-[#050308] border-white/5 p-4 sm:p-6 rounded-t-2xl sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4 sm:mb-6 gap-3">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-light text-white truncate">{detail.agency_name}</h2>
            <p className="text-xs sm:text-sm text-white/55 truncate">{detail.city} · {detail.director}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-agency-detail" className="shrink-0">Fermer</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Score</div>
            <div className={`text-xl font-medium ${m.score >= 80 ? "text-emerald-400" : m.score < 60 ? "text-red-400" : "text-white/90"}`}>{m.score}</div>
          </div>
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Tendance 30j</div>
            <div className="text-xl"><TrendArrow value={m.trend} /></div>
          </div>
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Process</div>
            <div className="text-xl text-white/90">{m.process_score}%</div>
          </div>
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Satisfaction</div>
            <div className="text-xl text-white/90">{m.satisfaction_rating}/5</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Adoption Homly</div>
            <div className="text-xl text-white/90">{m.adoption_score}%</div>
            <div className="text-xs text-white/30 mt-1">{m.active_users}/{m.total_users} actifs</div>
          </div>
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Dynamique</div>
            <div className="text-xl text-white/90">{m.dynamics_score}%</div>
          </div>
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Mandats actifs</div>
            <div className="text-xl text-white/90">{m.mandates_active}</div>
          </div>
          <div className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
            <div className="text-xs text-white/40">Δ Score 90j</div>
            <div className="text-xl"><TrendArrow value={detail.evolution.score_delta} /></div>
          </div>
        </div>

        <div className="mb-2 text-sm font-medium text-white/75 flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Recommandations Providence</div>
        <div className="space-y-2 mb-6">
          {detail.recommendations.map((r, i) => (
            <div key={i} className="p-3 bg-[#0c0814]/80 rounded border border-white/5">
              <div className="text-sm text-white/90 font-medium">{r.title}</div>
              <div className="text-xs text-white/55 mt-1">{r.detail}</div>
            </div>
          ))}
        </div>

        {/* MÉMOIRE MANAGÉRIALE — historique d'actions dirigeant sur cette agence */}
        {history && history.n_total > 0 && (
          <div className="mb-6 p-4 rounded-lg bg-gradient-to-br from-[#1a0f2e]/40 to-[#0c0814]/80 border border-[#A855F7]/15" data-testid="action-history">
            <div className="text-xs uppercase tracking-widest text-[#C084FC] font-medium mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Vos actions sur cette agence</div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-2 rounded bg-white/[0.02]">
                <div className="text-[10px] text-white/40 uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Briefs générés</div>
                <div className="text-xl text-white/90 mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>{history.stats?.brief_generated?.count || 0}</div>
              </div>
              <div className="text-center p-2 rounded bg-white/[0.02]">
                <div className="text-[10px] text-white/40 uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Consultations</div>
                <div className="text-xl text-white/90 mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>{history.stats?.view_fiche?.count || 0}</div>
              </div>
              <div className="text-center p-2 rounded bg-white/[0.02]">
                <div className="text-[10px] text-white/40 uppercase tracking-wider" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Alertes traitées</div>
                <div className="text-xl text-white/90 mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>{history.stats?.alert_dismissed?.count || 0}</div>
              </div>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {history.items.slice(0, 5).map((it, idx) => {
                const labels = {
                  brief_generated: { icon: "✨", label: "Brief Providence généré", color: "text-purple-300" },
                  view_fiche: { icon: "👁", label: "Fiche consultée", color: "text-white/65" },
                  alert_dismissed: { icon: "✓", label: "Alerte marquée traitée", color: "text-emerald-400" },
                  brief_copied: { icon: "📋", label: "Brief copié", color: "text-white/65" },
                  pdf_export: { icon: "📄", label: "Export PDF", color: "text-sky-400" },
                };
                const meta = labels[it.action_type] || { icon: "·", label: it.action_type, color: "text-white/55" };
                const date = new Date(it.created_at);
                const now = new Date();
                const diffMin = Math.round((now - date) / 60000);
                const rel = diffMin < 1 ? "à l'instant"
                  : diffMin < 60 ? `il y a ${diffMin} min`
                  : diffMin < 1440 ? `il y a ${Math.round(diffMin / 60)} h`
                  : `il y a ${Math.round(diffMin / 1440)} j`;
                return (
                  <div key={idx} className="flex items-center justify-between text-xs py-1">
                    <span className={`${meta.color}`}>{meta.icon} {meta.label}</span>
                    <span className="text-white/30" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{rel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="border-t border-white/5 pt-4 space-y-3">
          {!brief && (
            <Button onClick={generateBrief} disabled={briefLoading} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90" data-testid="generate-director-brief-btn">
              {briefLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Providence prépare le brief…</> : <><Sparkles className="w-4 h-4 mr-2" />Préparer un point avec le directeur</>}
            </Button>
          )}
          {brief && (
            <div className="bg-gradient-to-br from-purple-950/40 to-pink-950/40 border border-purple-500/30 rounded-lg p-4" data-testid="director-brief-content">
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs uppercase tracking-widest text-purple-300 font-medium">Brief Providence · Prêt à coller</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={copyBrief} data-testid="copy-brief-btn">{copied ? "Copié ✓" : "Copier"}</Button>
                  <Button size="sm" variant="ghost" onClick={generateBrief} disabled={briefLoading}>Régénérer</Button>
                </div>
              </div>
              <div className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">{brief}</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

// ====================================================================
// PROVIDENCE CHAT
// ====================================================================
const ProvidenceView = ({ groupId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (q) => {
    if (!q.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/providence/ask`, { question: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Erreur Providence : " + (e.response?.data?.detail || e.message) }]);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const { data } = await api.get(`/groups/${groupId}/providence/summary`);
      setSummary(data.summary);
    } catch (e) {
      setSummary("Erreur : " + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingSummary(false);
    }
  };

  const suggestions = [
    "Quelles agences nécessitent mon attention ce mois-ci ?",
    "Quelle agence pourrait servir de modèle aux autres ?",
    "Quels directeurs dois-je appeler cette semaine ?",
    "Comment améliorer l'homogénéité du groupe ?",
    "Où observe-t-on une baisse d'adoption de Homly ?",
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="providence-view">
      <div className="lg:col-span-2">
        <Card className="bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15 h-[680px] flex flex-col">
          <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-white/90 font-medium">Providence</div>
              <div className="text-xs text-white/40">IA de pilotage stratégique · Claude Sonnet 4.5</div>
            </div>
          </div>
          <ScrollArea className="flex-1 px-5 py-4" ref={scrollRef}>
            {messages.length === 0 && !loading && (
              <div className="text-center py-12 text-white/40" data-testid="providence-empty">
                <Sparkles className="w-8 h-8 mx-auto mb-3 text-white/20" />
                <div className="text-sm">Pose ta première question à Providence.</div>
                <div className="text-xs mt-1">Vision direction. Pas de micro-management.</div>
              </div>
            )}
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.role}-${i}`}>
                  <div className={`max-w-[85%] px-4 py-3 rounded-lg text-sm whitespace-pre-wrap ${
                    msg.role === "user" ? "bg-emerald-600/20 text-emerald-50 border border-emerald-700/30" : "bg-[#1a0f2e]/80 text-white/90 border border-white/10"
                  }`}>{msg.content}</div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start" data-testid="providence-thinking">
                  <div className="px-4 py-3 rounded-lg bg-[#1a0f2e]/80 border border-white/10 text-white/55 text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />Providence analyse…
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <div className="px-5 py-4 border-t border-white/5">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send(input)}
                placeholder="Pose une question stratégique à Providence…"
                disabled={loading}
                className="bg-[#0c0814]/80 border-white/10"
                data-testid="providence-input"
              />
              <Button onClick={() => send(input)} disabled={loading || !input.trim()} data-testid="providence-send">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
          <div className="text-sm font-medium text-white/75 mb-3">Questions suggérées</div>
          <div className="space-y-2">
            {suggestions.map((s, i) => (
              <button key={i} onClick={() => send(s)} disabled={loading} className="w-full text-left text-xs text-white/55 hover:text-emerald-400 hover:bg-white/5/50 px-3 py-2 rounded transition-colors" data-testid={`suggestion-${i}`}>
                {s}
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm font-medium text-white/75">Synthèse mensuelle</div>
            <Button variant="outline" size="sm" onClick={loadSummary} disabled={loadingSummary} data-testid="load-summary-btn">
              {loadingSummary ? <Loader2 className="w-3 h-3 animate-spin" /> : "Générer"}
            </Button>
          </div>
          {summary ? (
            <div className="text-xs text-white/75 whitespace-pre-wrap max-h-80 overflow-y-auto" data-testid="summary-content">{summary}</div>
          ) : (
            <div className="text-xs text-white/40">Cliquer pour générer une synthèse stratégique du mois.</div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ====================================================================
// RAPPORTS
// ====================================================================
const ReportsView = ({ reports, groupId }) => {
  const [downloading, setDownloading] = useState(null);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const response = await api.get(`/groups/${groupId}/export/${kind}`, { responseType: "blob" });
      const cd = response.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `groupe-export.${kind}`;
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(null);
    }
  };

  if (!reports) return null;
  return (
    <div className="space-y-6" data-testid="reports-view">
      <Card className="p-5 bg-gradient-to-br from-purple-950/40 to-pink-950/30 border-purple-500/20">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-purple-300 font-medium mb-1">Exports rapides</div>
            <div className="text-lg text-white/90">Téléchargez la synthèse complète</div>
            <div className="text-xs text-white/55 mt-1">PDF : KPI + comparatif + alertes + analyse Providence · CSV : comparatif agences (Excel-ready)</div>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Button onClick={() => download("pdf")} disabled={downloading === "pdf"} className="flex-1 md:flex-none bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90" data-testid="export-pdf-btn">
              {downloading === "pdf" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />PDF…</> : <><FileText className="w-4 h-4 mr-2" />Export PDF</>}
            </Button>
            <Button onClick={() => download("csv")} disabled={downloading === "csv"} variant="outline" className="flex-1 md:flex-none" data-testid="export-csv-btn">
              {downloading === "csv" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />CSV…</> : "Export CSV"}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {reports.available.map((r) => (
          <Card key={r.id} className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15" data-testid={`report-${r.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <FileText className="w-5 h-5 text-emerald-400 mt-0.5" />
              <div className="flex-1">
                <div className="text-white/90 font-medium">{r.title}</div>
                <div className="text-xs text-white/40 mt-1">{r.period}</div>
              </div>
            </div>
            <p className="text-sm text-white/55 mb-4">{r.description}</p>
            <Button variant="outline" size="sm" className="w-full" onClick={() => download(r.id === "agencies-comparison" ? "csv" : "pdf")}>
              Télécharger
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ====================================================================
// PARAMÈTRES
// ====================================================================
const SettingsView = ({ groupId }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState("");
  const [busyAgency, setBusyAgency] = useState(false);

  const loadAll = async () => {
    const [s, av, ag] = await Promise.all([
      api.get(`/groups/${groupId}/settings`),
      api.get(`/groups/${groupId}/available-agencies`),
      api.get(`/groups/${groupId}/agencies`),
    ]);
    setSettings(s.data);
    setAvailable(av.data.available);
    setAgencies(ag.data.agencies);
  };

  useEffect(() => { loadAll(); }, [groupId]); // eslint-disable-line

  if (!settings) return <Skeleton className="h-64" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/groups/${groupId}/settings`, {
        name: settings.name,
        alert_thresholds: settings.alert_thresholds,
        default_period_days: settings.default_period_days,
      });
    } finally { setSaving(false); }
  };

  const attachAgency = async () => {
    if (!selectedAgency) return;
    setBusyAgency(true);
    try {
      await api.post(`/groups/${groupId}/agencies`, { agency_id: selectedAgency });
      setSelectedAgency("");
      await loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    } finally { setBusyAgency(false); }
  };

  const detachAgency = async (agency_id) => {
    if (!window.confirm("Détacher cette agence du groupe ? L'agence n'est pas supprimée.")) return;
    setBusyAgency(true);
    try {
      await api.delete(`/groups/${groupId}/agencies/${agency_id}`);
      await loadAll();
    } finally { setBusyAgency(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="settings-view">
      <div>
        <h2 className="text-lg text-white/90">Paramètres du groupe</h2>
        <p className="text-sm text-white/55 mt-1">Gérez vos agences rattachées, les seuils d&apos;alerte et l&apos;identité du groupe.</p>
      </div>

      <GroupBillingCard />

      <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
        <div className="text-sm font-medium text-white/75 mb-1">Identité du groupe</div>
        <div className="text-xs text-white/40 mb-4">Apparaît dans le header, les exports PDF et les briefs Providence.</div>
        <label className="text-xs text-white/40 block mb-1">Nom</label>
        <Input value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} className="bg-[#0c0814]/80 border-white/10" data-testid="settings-name" />
      </Card>

      <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
        <div className="text-sm font-medium text-white/75 mb-1">Agences rattachées · {agencies.length}</div>
        <div className="text-xs text-white/40 mb-4">Les agences existantes que vous pilotez via ce dashboard.</div>
        <div className="space-y-2 mb-4">
          {agencies.map((a) => (
            <div key={a.agency_id} className="flex items-center justify-between bg-[#0c0814]/80 border border-white/5 rounded px-3 py-2" data-testid={`settings-agency-${a.agency_id}`}>
              <div>
                <div className="text-sm text-white/90">{a.agency_name}</div>
                <div className="text-xs text-white/40">{a.city} · {a.director}</div>
              </div>
              <Button size="sm" variant="ghost" disabled={busyAgency} onClick={() => detachAgency(a.agency_id)} className="text-red-400 hover:text-red-300" data-testid={`detach-${a.agency_id}`}>Détacher</Button>
            </div>
          ))}
        </div>
        <div className="border-t border-white/5 pt-4">
          <div className="text-xs text-white/40 mb-2">Ajouter une agence existante au groupe</div>
          <div className="flex gap-2">
            <select
              value={selectedAgency}
              onChange={(e) => setSelectedAgency(e.target.value)}
              className="flex-1 bg-[#0c0814]/80 border border-white/10 rounded px-3 py-2 text-sm text-white/90"
              data-testid="agency-select"
            >
              <option value="">— Choisir une agence ({available.length} disponibles) —</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>{a.name} {a.city ? `· ${a.city}` : ""}</option>
              ))}
            </select>
            <Button onClick={attachAgency} disabled={!selectedAgency || busyAgency} data-testid="attach-btn">Rattacher</Button>
          </div>
          <div className="text-[11px] text-white/40 mt-2">
            Astuce : une agence créée par un admin (rôle <span className="text-white/75">admin</span>) apparaîtra ici automatiquement, à condition qu&apos;elle ne soit pas déjà dans un autre groupe.
          </div>
        </div>
      </Card>

      <Card className="p-5 bg-gradient-to-br from-[#0c0814]/90 to-[#0f0a1e]/80 border-[#A855F7]/15">
        <div className="text-sm font-medium text-white/75 mb-1">Seuils d&apos;alerte</div>
        <div className="text-xs text-white/40 mb-4">À partir de quels niveaux Providence vous alerte automatiquement.</div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-white/40 block mb-1">Score &quot;à risque&quot; (sous)</label>
            <Input type="number" value={settings.alert_thresholds.score_at_risk} onChange={(e) => setSettings({ ...settings, alert_thresholds: { ...settings.alert_thresholds, score_at_risk: parseFloat(e.target.value) } })} className="bg-[#0c0814]/80 border-white/10" />
          </div>
          <div>
            <label className="text-xs text-white/40 block mb-1">Baisse tendance (%)</label>
            <Input type="number" value={settings.alert_thresholds.trend_drop} onChange={(e) => setSettings({ ...settings, alert_thresholds: { ...settings.alert_thresholds, trend_drop: parseFloat(e.target.value) } })} className="bg-[#0c0814]/80 border-white/10" />
          </div>
          <div>
            <label className="text-xs text-white/40 block mb-1">Adoption faible (sous %)</label>
            <Input type="number" value={settings.alert_thresholds.adoption_low} onChange={(e) => setSettings({ ...settings, alert_thresholds: { ...settings.alert_thresholds, adoption_low: parseFloat(e.target.value) } })} className="bg-[#0c0814]/80 border-white/10" />
          </div>
        </div>
      </Card>

      <Button onClick={save} disabled={saving} data-testid="settings-save">{saving ? "Enregistrement…" : "Enregistrer les seuils & l'identité"}</Button>
    </div>
  );
};

// ====================================================================
// MAIN PAGE
// ====================================================================
const NAV = [
  { id: "overview", label: "Vue groupe", icon: LayoutDashboard },
  { id: "agencies", label: "Agences", icon: Building2 },
  { id: "trends", label: "Tendances", icon: TrendingUp },
  { id: "process", label: "Process", icon: ListChecks },
  { id: "alerts", label: "Alertes", icon: AlertTriangle },
  { id: "missions", label: "Missions", icon: Target, badge: true },
  { id: "providence", label: "Providence", icon: Sparkles },
  { id: "reports", label: "Rapports", icon: FileText },
  { id: "settings", label: "Paramètres", icon: Settings },
];

export default function GroupDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [group, setGroup] = useState(null);
  const [overview, setOverview] = useState(null);
  const [agencies, setAgencies] = useState(null);
  const [trends, setTrends] = useState(null);
  const [processData, setProcessData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [reports, setReports] = useState(null);
  const [agencyDetail, setAgencyDetail] = useState(null);
  const [error, setError] = useState(null);
  const [missionsBadge, setMissionsBadge] = useState(null);

  useEffect(() => {
    api.get("/groups/me")
      .then((r) => {
        const g = r.data.group || (r.data.groups && r.data.groups[0]);
        if (!g) throw new Error("Aucun groupe disponible");
        setGroup(g);
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    if (!group) return;
    const gid = group.id;
    // PREFETCH PARALLÈLE — zéro latence inter-onglets
    Promise.all([
      api.get(`/groups/${gid}/overview`).then((r) => setOverview(r.data)).catch(() => {}),
      api.get(`/groups/${gid}/agencies`).then((r) => setAgencies(r.data.agencies)).catch(() => {}),
      api.get(`/groups/${gid}/trends`).then((r) => setTrends(r.data)).catch(() => {}),
      api.get(`/groups/${gid}/process`).then((r) => setProcessData(r.data)).catch(() => {}),
      api.get(`/groups/${gid}/alerts`).then((r) => setAlerts(r.data)).catch(() => {}),
      api.get(`/groups/${gid}/reports`).then((r) => setReports(r.data)).catch(() => {}),
      api.get(`/groups/${gid}/missions-badge`).then((r) => setMissionsBadge(r.data)).catch(() => {}),
    ]);
  }, [group]);

  const openAgency = (aid) => {
    api.get(`/groups/${group.id}/agencies/${aid}`).then((r) => setAgencyDetail(r.data));
  };

  if (error) {
    return <div className="min-h-screen bg-[#050308] flex items-center justify-center text-white/75" data-testid="group-error">
      <Card className="p-8 bg-[#0c0814]/80 border-white/5 max-w-md">
        <h2 className="text-xl mb-3">Accès refusé</h2>
        <p className="text-sm text-white/55 mb-4">{error}</p>
        <Button onClick={() => navigate("/login")}>Se connecter</Button>
      </Card>
    </div>;
  }
  if (!group) return <div className="min-h-screen bg-[#050308] flex items-center justify-center" data-testid="group-loading"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;

  return (
    <div className="min-h-screen bg-[#050308] text-white/90 relative overflow-x-hidden">
      {/* Halo background subtil */}
      <div className="fixed top-0 right-0 w-[600px] h-[600px] bg-[#A855F7]/8 rounded-full blur-[180px] pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-[500px] h-[500px] bg-[#FF914D]/5 rounded-full blur-[160px] pointer-events-none" />

      {/* Header */}
      <div className="relative border-b border-white/5 bg-[#050308]/90 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-[#A855F7] via-[#C084FC] to-[#FF914D] flex items-center justify-center shadow-lg shadow-[#A855F7]/30 shrink-0">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl text-white tracking-tight truncate" style={DISPLAY} data-testid="group-name">{group.name}</h1>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1.5 sm:gap-2 mt-0.5 flex-wrap" style={MONO}>
                  <span>{group.agency_ids.length} AGENCES</span>
                  {overview && <><span className="hidden sm:inline">·</span><span>Score : {overview.metrics.score_groupe}/100</span></>}
                  {overview && <><span className="hidden sm:inline">·</span><span className={overview.metrics.trend >= 0 ? "text-emerald-400" : "text-red-400"}>{overview.metrics.trend > 0 ? "+" : ""}{overview.metrics.trend}%</span></>}
                </p>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" className="shrink-0 px-2 sm:px-3">
            <LogOut className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Déconnexion</span>
          </Button>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Tabs nav */}
        <div className="flex gap-1 mb-4 sm:mb-8 overflow-x-auto border-b border-white/5 pb-px scrollbar-hide" data-testid="group-nav">
          {NAV.map((n) => {
            const Icon = n.icon;
            const badgeTotal = n.badge && missionsBadge ? missionsBadge.total : 0;
            return (
              <button
                key={n.id}
                onClick={() => setTab(n.id)}
                className={`relative px-3 sm:px-4 py-2.5 text-[10px] sm:text-xs uppercase tracking-widest flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap ${
                  tab === n.id ? "border-[#A855F7] text-[#C084FC]" : "border-transparent text-white/40 hover:text-white/85"
                }`}
                style={MONO}
                data-testid={`nav-${n.id}`}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />{n.label}
                {badgeTotal > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold tabular-nums min-w-[18px] text-center" data-testid="missions-badge">
                    {missionsBadge.display}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {tab === "overview" && (overview ? <OverviewView overview={overview} onOpenAgency={openAgency} groupId={group.id} onGoMissions={() => setTab("missions")} /> : <Skeleton className="h-96 bg-[#0c0814]/80" />)}
        {tab === "agencies" && (agencies ? <AgenciesView agencies={agencies} onOpenAgency={openAgency} /> : <Skeleton className="h-96 bg-[#0c0814]/80" />)}
        {tab === "trends" && (trends ? <TrendsView trends={trends} /> : <Skeleton className="h-96 bg-[#0c0814]/80" />)}
        {tab === "process" && (processData ? <ProcessView processData={processData} onOpenAgency={openAgency} groupId={group.id} /> : <Skeleton className="h-96 bg-[#0c0814]/80" />)}
        {tab === "alerts" && (alerts ? <AlertsView alertsData={alerts} agencies={agencies} onOpenAgency={openAgency} groupId={group.id} /> : <Skeleton className="h-96 bg-[#0c0814]/80" />)}
        {tab === "missions" && <MissionsView groupId={group.id} onOpenAgency={openAgency} />}
        {tab === "providence" && <ProvidenceView groupId={group.id} />}
        {tab === "reports" && (reports ? <ReportsView reports={reports} groupId={group.id} /> : <Skeleton className="h-96 bg-[#0c0814]/80" />)}
        {tab === "settings" && <SettingsView groupId={group.id} />}
      </div>

      {agencyDetail && <AgencyDetailPanel detail={agencyDetail} groupId={group.id} onClose={() => setAgencyDetail(null)} />}
    </div>
  );
}
